/*
 * Miles header nav.
 *
 * Vanilla custom element in the Dawn idiom — no build step, no framework. Deliberately
 * does not reuse Dawn's menu-drawer: that component keys off a 990px breakpoint and
 * ships its own focus-trap assumptions, and mixing it with the 860px design breakpoint
 * produces a band where neither nav is correct.
 *
 * Accessibility contract:
 *   - the toggle owns aria-expanded
 *   - the panel is inert while closed, so its links stay out of the tab order without
 *     display:none (which would kill the open/close transition)
 *   - Escape closes and returns focus to the toggle
 *   - focus moves to the first panel link on open
 *   - crossing the breakpoint upward resets state, so a panel left open on a phone
 *     does not linger invisibly after a rotate to tablet width
 */
class MilesHeader extends HTMLElement {
  constructor() {
    super();
    // Bound ONCE, in the constructor, not per connect. Binding inside
    // connectedCallback mints a fresh function reference every time, so the
    // reference passed to removeEventListener would never match the one that was
    // added — the removal silently no-ops and handlers stack up.
    this.onToggle = this.onToggle.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onScroll = this.onScroll.bind(this);
  }

  connectedCallback() {
    this.cartLink = this.querySelector('#cart-icon-bubble');
    window.addEventListener('pageshow', this.onPageShow);
    // Compact-on-scroll. Set up BEFORE the toggle/panel guard below: the launch
    // header is a wordmark and a button with no menu at all, and it is exactly
    // the configuration where 89px of permanently fixed bar is hardest to
    // justify. Only worth doing when the bar is actually pinned.
    this.compactQueued = false;
    this.compacted = false;
    if (this.classList.contains('miles-header--sticky')) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      // Apply the starting state directly rather than through onScroll: a reload
      // partway down the page starts already scrolled, and there is no frame to
      // wait for yet. It also keeps connectedCallback off requestAnimationFrame,
      // which a headless DOM need not provide — a throw here would abort the
      // connect before the nav below is wired, and take the whole header with it.
      this.setCompact(window.scrollY > 96);
    }

    this.toggle = this.querySelector('.miles-header__toggle');
    this.panel = this.querySelector('.miles-header__panel');
    if (!this.toggle || !this.panel) return;

    this.breakpoint = Number(this.dataset.breakpoint) || 860;
    this.mq = window.matchMedia(`(min-width: ${this.breakpoint + 1}px)`);

    this.toggle.addEventListener('click', this.onToggle);
    document.addEventListener('keydown', this.onKeydown);
    this.mq.addEventListener('change', this.onViewportChange);

    this.close({ restoreFocus: false });
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('scroll', this.onScroll);
    this.cartRefresh?.abort();
    // The toggle is a child, so on REPLACEMENT it is discarded with the subtree and
    // this removal is redundant. It matters on disconnect-then-reconnect of the same
    // instance — a move rather than a replace — where connectedCallback runs again on
    // a toggle that still exists. Without this, each cycle stacks another click
    // handler and the button starts toggling twice per press.
    this.toggle?.removeEventListener('click', this.onToggle);
    document.removeEventListener('keydown', this.onKeydown);
    this.mq?.removeEventListener('change', this.onViewportChange);
    // The scroll lock lives on <body>, which outlives this element. The theme editor
    // tears the section down and rebuilds it on every setting change, so if the panel
    // was open at that moment the class would survive on a body no element owns any
    // more — the page stays unscrollable until a full reload.
    document.body.classList.remove('miles-nav-open');
  }

  // Throttled to one class check per frame. scroll fires far faster than paint
  // on iOS momentum scrolling, and the listener is passive so it never blocks it.
  onScroll() {
    if (this.compactQueued) return;
    this.compactQueued = true;
    requestAnimationFrame(() => {
      this.compactQueued = false;
      // Past the bar's own height: the moment the header would have scrolled
      // away if it were not fixed is the moment it earns being smaller.
      this.setCompact(window.scrollY > 96);
    });
  }

  // The class is only written when the state actually flips, so a long scroll is
  // one style recalc rather than hundreds.
  setCompact(next) {
    if (next === this.compacted) return;
    this.compacted = next;
    this.classList.toggle('miles-header--compact', next);
  }

  async onPageShow(event) {
    // Back/Forward can restore a header rendered before a cart edit. Normal
    // navigation already receives the current count from Liquid.
    if (!event.persisted || !this.cartLink) return;
    this.cartRefresh?.abort();
    const controller = new AbortController();
    this.cartRefresh = controller;
    const url = new URL(this.cartLink.href);
    url.searchParams.set('section_id', 'cart-icon-bubble');
    try {
      const response = await fetch(url.href, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) return;
      const content = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('.shopify-section');
      if (content && this.isConnected && !controller.signal.aborted) this.cartLink.innerHTML = content.innerHTML;
    } catch (_) {
      // The cart remains a working link if the count refresh is unavailable.
    }
  }

  get isOpen() {
    return this.toggle.getAttribute('aria-expanded') === 'true';
  }

  onToggle() {
    this.isOpen ? this.close() : this.open();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close();
    }
  }

  onViewportChange(event) {
    // Above the breakpoint the panel is not the nav any more; drop any open state.
    if (event.matches && this.isOpen) this.close({ restoreFocus: false });
  }

  open() {
    this.toggle.setAttribute('aria-expanded', 'true');
    this.classList.add('miles-header--open');
    this.panel.inert = false;
    document.body.classList.add('miles-nav-open');
    this.panel.querySelector('a')?.focus();
  }

  close({ restoreFocus = true } = {}) {
    this.toggle.setAttribute('aria-expanded', 'false');
    this.classList.remove('miles-header--open');
    this.panel.inert = true;
    document.body.classList.remove('miles-nav-open');
    if (restoreFocus) this.toggle.focus();
  }
}

if (!customElements.get('miles-header')) {
  customElements.define('miles-header', MilesHeader);
}
